/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/database/_rail.tsx`.
 *
 * Changes: next/link + next/navigation → @excellent/ui/router. Pushes a
 * side:"right" tree into ContextRail so /database lights the reserved right
 * margin. Mounted from RootShell when the path is under /database (the Next
 * host mounts it from app/database/layout.tsx).
 */

import { useMemo } from "react";
import {
  cn,
  useProvideRailExtension,
  type RailExtension,
  type RailExtensionTreeGroup,
} from "@excellent/ui";
import {
  Copy,
  Boxes,
  Database,
  GitMerge,
  Layers,
  ListFilter,
  type LucideIcon,
  RefreshCw,
  Share2,
  Table2,
  TerminalSquare,
  Upload,
} from "@excellent/ui/icons";
import { RouterLink as Link, usePathname } from "@excellent/ui/router";

/**
 * The Database section navigation, floated into the shell's right-gutter
 * `ContextRail` — the SAME mechanism Settings, Agents ▸ Analytics, and the docs
 * reader use.
 *
 * On desktop this renders nothing into the page body: it pushes a `side:"right"`
 * rail extension (`useProvideRailExtension`) and the shell's `ContextRail` draws
 * it in the right gutter — a `reserve`-placed compact rail symmetric with the
 * left `ModeRail`, so the page body stays FULL-WIDTH and left-aligned like every
 * other surface.
 *
 * Below `lg` the rail hides; this component renders an in-body horizontal nav
 * strip as the fallback (mirrors `app/settings/_rail.tsx`).
 */

interface DatabaseDest {
  href: string;
  label: string;
  icon: LucideIcon;
}

const DATABASE_DESTS: DatabaseDest[] = [
  { href: "/database", label: "Overview", icon: Database },
  { href: "/database/import", label: "Import", icon: Upload },
  { href: "/database/objects", label: "Objects", icon: Boxes },
  { href: "/database/views", label: "Views", icon: ListFilter },
  { href: "/database/editor", label: "Table Editor", icon: Table2 },
  { href: "/database/sql", label: "SQL Editor", icon: TerminalSquare },
  { href: "/database/schema", label: "Schema", icon: Share2 },
  { href: "/database/migrations", label: "Migrations", icon: Layers },
  { href: "/database/sync", label: "Fleet Sync", icon: RefreshCw },
  { href: "/database/conflicts", label: "Sync Conflicts", icon: GitMerge },
  { href: "/database/duplicates", label: "Duplicates", icon: Copy },
];

/** Active = exact for the Overview root, else prefix (so `/editor/[table]` lights
 *  Table Editor). Mirrors the retired left rail's matcher. */
function isActive(pathname: string, href: string): boolean {
  return href === "/database"
    ? pathname === "/database"
    : pathname === href || pathname.startsWith(href + "/");
}

export function DatabaseRail() {
  const pathname = usePathname();

  // A flat tree: each destination is a top-level group row (no nested pages),
  // under the "Database" heading — the same shape Settings uses for its
  // top-level groups, so both sides of the app share one right-rail grammar.
  const extension = useMemo<RailExtension>(() => {
    const tree: RailExtensionTreeGroup[] = DATABASE_DESTS.map((d) => {
      const Icon = d.icon;
      return {
        key: d.href,
        label: d.label,
        href: d.href,
        icon: <Icon strokeWidth={1.5} className="size-3.5" />,
        active: isActive(pathname, d.href),
        consolidated: false,
        items: [],
      };
    });
    return {
      side: "right",
      placement: "reserve",
      groupLabel: "Database",
      activeHref:
        DATABASE_DESTS.find((d) => isActive(pathname, d.href))?.href ?? "",
      links: [],
      toc: [],
      tree,
    };
  }, [pathname]);

  useProvideRailExtension(extension);

  // < lg fallback: a horizontal strip of the destinations rendered in the page
  // body (the desktop rail is hidden there). Mirrors the settings MobileNav.
  return (
    <nav
      aria-label="Database"
      className="flex gap-1 overflow-x-auto px-6 py-2 no-scrollbar lg:hidden"
    >
      {DATABASE_DESTS.map((d) => {
        const active = isActive(pathname, d.href);
        return (
          <Link
            key={d.href}
            href={d.href}
            className={cn(
              "inline-flex h-7 items-center whitespace-nowrap rounded-full px-3 type-body transition-colors",
              active
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            )}
          >
            {d.label}
          </Link>
        );
      })}
    </nav>
  );
}
